import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { ContentCard } from '../../../../components/common/ContentCard';
import { CheckCircle, ChevronDown, ChevronUp, Lock } from 'react-feather';
import type {
  PostSessionCommitment,
  PostSessionParticipant,
} from '../../../../services/api/coachPostSession';
import {
  getAvatarColor,
  getInitialsFromParts,
} from '../../../../utils/avatarHelpers';

/**
 * MED-794 AC5 / MED-1052 / MED-1216 — Log Commitments card (shared by the coach
 * and peer-leader post-session pages).
 *
 * Per-participant commitment entry. MED-1216 replaces the old
 * auto-save-on-keystroke / whole-roster background flush with an explicit
 * per-row Save button and adds authorship awareness:
 *
 *   • No blank saves (AC1): the Save button is disabled with a tooltip until the
 *     field has content; the server rejects empty as a backstop.
 *   • Per-row saves (AC6): saving one participant never rewrites another's row.
 *   • Coach ownership lock (AC4/AC8): a participant-authored row
 *     (`authored_by_user_id === participant_user_id`) is read-only to the coach
 *     and labeled "saved by participant". The coach may still fill in a
 *     commitment for a participant who hasn't entered one (AC2/AC3).
 *   • Stale-coach protection (AC7): when a coach save is rejected because the
 *     participant saved their own in the meantime, the parent refreshes the row
 *     to the participant's entry and passes a `rowNotices` message shown here.
 *
 * Peer-leader self mode (`leaderUserId` set): the single row is the leader's own
 * commitment — never locked (self-authored is theirs to edit) — with only the
 * no-blank rule applied (MED-1133/E4.5a: self-only, no coach fill-in/lock).
 */

const TITLE_COLOR = '#1A1A2E';
const SUBTITLE_COLOR = '#9CA3AF';
const NAME_COLOR = '#374151';
const FIELD_BORDER = '#E5E7EB';
const FIELD_TEXT = '#374151';
const ROW_BORDER = '#F3F4F6';
const ATTRIBUTION_COLOR = '#6B7280';
const NOTICE_COLOR = '#B45309';

interface Props {
  participants: PostSessionParticipant[];
  /** Server-truth commitment rows for this session (0..1 per participant). */
  commitments: PostSessionCommitment[];
  /** Save one participant's commitment. Resolves true on success. */
  onSave: (participantId: string, text: string) => Promise<boolean>;
  /** user_id whose row is currently saving (per-row spinner), or null. */
  savingParticipantId: string | null;
  /**
   * MED-1216 / AC7 — per-participant notices keyed by user_id, set after a coach
   * save was rejected because the participant now owns the row.
   */
  rowNotices?: Record<string, string>;
  /**
   * MED-817 AC8 / MED-1216 — the peer leader's own user_id. When set the card is
   * in self mode: the leader's row is always editable (never locked) and the
   * "You" badge is shown. The coach view never sets this.
   */
  leaderUserId?: string;
}

export function LogCommitmentsCard({
  participants,
  commitments,
  onSave,
  savingParticipantId,
  rowNotices,
  leaderUserId,
}: Props) {
  // MED-1077 — manual collapse only (no honest completion signal): loads
  // expanded, the chevron is always available to collapse/expand.
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const commitmentByParticipant = new Map<string, PostSessionCommitment>(
    commitments.map(c => [c.participant_user_id, c]),
  );

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      header={
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group gap={8} align="center" wrap="nowrap">
              <CheckCircle size={18} color={TITLE_COLOR} aria-hidden="true" />
              <Text fz={15} fw={700} c={TITLE_COLOR}>
                Log Commitments
              </Text>
            </Group>
            {/* MED-1247 — the subtitle describes logging every participant's
                commitment, which is only true in the coach view. In peer-leader
                self mode (`leaderUserId` set) the card shows just the leader's own
                row, so the subtitle would be misleading — suppress it there. */}
            {leaderUserId == null && (
              <Text fz={12} fw={500} c={SUBTITLE_COLOR}>
                What each participant committed to.
              </Text>
            )}
          </Stack>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expand commitments' : 'Collapse commitments'}
          >
            {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </ActionIcon>
        </Group>
      }
    >
      {!collapsed && (
        <Stack gap={0}>
          {participants.map((p, idx) => {
            const isLeaderSelf = leaderUserId != null && p.user_id === leaderUserId;
            const row = commitmentByParticipant.get(p.user_id) ?? null;
            // AC4/AC8 — a participant-authored row is read-only to the coach.
            // Never lock in peer self mode (the leader's own row is theirs).
            const lockedToParticipant =
              !isLeaderSelf &&
              row != null &&
              row.authored_by_user_id === row.participant_user_id;
            return (
              <Box
                key={p.user_id}
                style={
                  idx < participants.length - 1
                    ? { borderBottom: `1px solid ${ROW_BORDER}`, paddingBottom: 13 }
                    : undefined
                }
                pt={idx === 0 ? 0 : 12}
              >
                <CommitmentRow
                  participant={p}
                  savedText={row?.commitment_text ?? ''}
                  lockedToParticipant={lockedToParticipant}
                  isLeaderSelf={isLeaderSelf}
                  saving={savingParticipantId === p.user_id}
                  notice={rowNotices?.[p.user_id] ?? null}
                  onSave={text => onSave(p.user_id, text)}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </ContentCard>
  );
}

interface CommitmentRowProps {
  participant: PostSessionParticipant;
  savedText: string;
  lockedToParticipant: boolean;
  isLeaderSelf: boolean;
  saving: boolean;
  notice: string | null;
  onSave: (text: string) => Promise<boolean>;
}

/**
 * One participant's commitment row. Owns the ephemeral edit draft; the parent
 * owns the saved server value + saving/notice state.
 */
function CommitmentRow({
  participant: p,
  savedText,
  lockedToParticipant,
  isLeaderSelf,
  saving,
  notice,
  onSave,
}: CommitmentRowProps) {
  const [draft, setDraft] = useState<string>(savedText);
  const [justSaved, setJustSaved] = useState<boolean>(false);

  // Re-seed the draft when the server value changes underneath us (own save
  // reconcile, or an AC7 refresh that flips the row to participant-owned). The
  // server value only changes after a save, so this never clobbers live typing.
  useEffect(() => {
    setDraft(savedText);
    setJustSaved(false);
  }, [savedText]);

  const initials = getInitialsFromParts(p.first_name, p.last_name);
  const color = getAvatarColor(p.user_id);
  const trimmed = draft.trim();
  const canSave = trimmed.length > 0 && !saving;
  const isDirty = trimmed !== savedText.trim();

  return (
    <Stack gap={10}>
      <Group gap={10} align="center" wrap="nowrap">
        <Avatar color={color} radius="xl" size={28}>
          {initials}
        </Avatar>
        <Text fz={13} fw={700} c={NAME_COLOR}>
          {p.first_name} {p.last_name}
        </Text>
        {isLeaderSelf && (
          <Badge color="medleyGreen" variant="light" radius="sm" size="sm">
            You
          </Badge>
        )}
        {lockedToParticipant && (
          <Badge
            color="gray"
            variant="light"
            radius="sm"
            size="sm"
            leftSection={<Lock size={10} aria-hidden="true" />}
          >
            Saved by participant
          </Badge>
        )}
      </Group>

      {lockedToParticipant ? (
        // AC8 — participant-authored: read-only, attributed, no Save.
        <Stack gap={4}>
          <Box
            style={{
              borderLeft: `3px solid ${FIELD_BORDER}`,
              paddingLeft: 10,
            }}
          >
            <Text fz={13} c={FIELD_TEXT} style={{ whiteSpace: 'pre-wrap' }}>
              {savedText}
            </Text>
          </Box>
          <Text fz={11} c={ATTRIBUTION_COLOR}>
            Saved by participant — this is their own commitment and can’t be changed here.
          </Text>
          {notice && (
            <Text fz={11} c={NOTICE_COLOR}>
              {notice}
            </Text>
          )}
        </Stack>
      ) : (
        <Stack gap={8}>
          <Textarea
            value={draft}
            onChange={e => setDraft(e.currentTarget.value)}
            placeholder={
              isLeaderSelf
                ? 'What did you commit to?'
                : `What did ${p.first_name} commit to?`
            }
            autosize
            minRows={1}
            maxRows={4}
            disabled={saving}
            styles={{
              input: {
                borderColor: FIELD_BORDER,
                borderRadius: 8,
                color: FIELD_TEXT,
                fontSize: 13,
                lineHeight: 1.5,
              },
            }}
          />
          {notice && (
            <Text fz={11} c={NOTICE_COLOR}>
              {notice}
            </Text>
          )}
          <Group gap={10} align="center" justify="flex-end" wrap="nowrap">
            {justSaved && !isDirty && (
              <Group gap={6} align="center" wrap="nowrap">
                <Box
                  w={8}
                  h={8}
                  style={{
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-medleyGreen-6)',
                  }}
                />
                <Text size="xs" c="dimmed">
                  Saved
                </Text>
              </Group>
            )}
            {/* AC1 — Save disabled until non-empty; tooltip explains why. The Box
                wrapper lets the tooltip show even while the button is disabled. */}
            <Tooltip
              label="Enter a commitment before saving."
              disabled={canSave || saving}
              withArrow
              position="top"
            >
              <Box style={{ display: 'inline-block' }}>
                <Button
                  size="xs"
                  color="medleyGreen"
                  disabled={!canSave}
                  loading={saving}
                  onClick={async () => {
                    const ok = await onSave(trimmed);
                    if (ok) setJustSaved(true);
                  }}
                >
                  Save
                </Button>
              </Box>
            </Tooltip>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

export default LogCommitmentsCard;
